/**
 * @ipc/admin-ui · widgets/app-shell · profile-menu — футер-профиль навбара с поповером.
 * Триггер: аватар-инициал + Telegram username + роль. Внутри поповера — селект Темы,
 * селект Языка и кнопка Выйти (мутация logout). Тема пишется в .dark + cookie через useTheme.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, LogOut, Sun, Languages, Building2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
} from '@/shared/ui';
import { useTheme } from '@/shared/lib';
import type { Theme } from '@/shared/lib';
import { useTenantId, useSwitchTenant, setTenantId } from '@/shared/api';
import { useTenantsQuery } from '@/entities/tenant';
import { useLogoutMutation } from '@/entities/session';
import type { SessionUser } from '@/entities/session';

function initialOf(source: string): string {
  const ch = source.trim().charAt(0);
  return ch === '' ? '?' : ch.toUpperCase();
}

export function ProfileMenu({ user }: { user: SessionUser }) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const logoutMutation = useLogoutMutation();
  const roleLabel = user.role ?? 'Администратор';

  const tenantId = useTenantId();
  const switchTenant = useSwitchTenant();
  const tenantsQuery = useTenantsQuery();
  const tenants = tenantsQuery.data ?? [];

  // Если сохранённый/дефолтный тенант отсутствует в реестре (напр. сборочный
  // 'default' без такого slug) — выбрать тенанта по умолчанию или первого из списка.
  useEffect(() => {
    if (tenants.length === 0) {
      return;
    }
    if (!tenants.some((t) => t.slug === tenantId)) {
      const fallback = tenants.find((t) => t.default) ?? tenants[0];
      if (fallback !== undefined) {
        setTenantId(fallback.slug);
      }
    }
  }, [tenants, tenantId]);

  function onThemeChange(value: string) {
    const next: Theme = value === 'light' ? 'light' : 'dark';
    setTheme(next);
  }

  function onLogout() {
    logoutMutation.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-sidebar-accent"
          aria-label="Меню пользователя"
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground uppercase">
            {initialOf(user.tgUsername)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-sidebar-foreground">
              @{user.tgUsername}
            </span>
            <span className="block truncate text-xs text-muted-foreground">{roleLabel}</span>
          </span>
          <ChevronUp className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-[212px]">
        {tenants.length > 0 ? (
          <>
            <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
              <Building2 className="size-3.5" /> Тенант
            </DropdownMenuLabel>
            <div className="px-2 pb-2">
              <Select value={tenantId} onValueChange={switchTenant}>
                <SelectTrigger className="w-full" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
          <Sun className="size-3.5" /> Тема
        </DropdownMenuLabel>
        <div className="px-2 pb-2">
          <Select value={theme} onValueChange={onThemeChange}>
            <SelectTrigger className="w-full" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dark">Тёмная</SelectItem>
              <SelectItem value="light">Светлая</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
          <Languages className="size-3.5" /> Язык
        </DropdownMenuLabel>
        <div className="px-2 pb-2">
          <Select value="ru">
            <SelectTrigger className="w-full" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ru">Русский</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DropdownMenuSeparator />
        <div className="p-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={onLogout}
            disabled={logoutMutation.isPending}
          >
            <LogOut className="size-4" /> Выйти
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
